import * as React from "react"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"

type KpiTone = "primary" | "accent" | "success" | "warning" | "danger" | "neutral"

const toneClasses: Record<KpiTone, { bg: string; text: string }> = {
  primary: { bg: "bg-primary-50 dark:bg-primary-900/40", text: "text-primary-700 dark:text-primary-300" },
  accent: { bg: "bg-accent-100 dark:bg-accent-900/40", text: "text-accent-700 dark:text-accent-300" },
  success: { bg: "bg-success-50 dark:bg-success/15", text: "text-success-700 dark:text-success-300" },
  warning: { bg: "bg-warning-50 dark:bg-warning/15", text: "text-warning-700 dark:text-warning-300" },
  danger: { bg: "bg-danger-50 dark:bg-danger/15", text: "text-danger-700 dark:text-danger-300" },
  neutral: { bg: "bg-neutral-100 dark:bg-neutral-800", text: "text-neutral-700 dark:text-neutral-300" },
}

interface KpiCardProps {
  title: string
  value: React.ReactNode
  icon?: React.ReactNode
  tone?: KpiTone
  delta?: React.ReactNode
  deltaLabel?: string
  loading?: boolean
  className?: string
  onClick?: () => void
}

/**
 * KPI card mobile-first: stack vertical full-width.
 * Icono colored bg, valor grande, descripción debajo, delta en footer.
 */
export default function KpiCard({
  title,
  value,
  icon,
  tone = "primary",
  delta,
  deltaLabel,
  loading,
  className,
  onClick,
}: KpiCardProps) {
  const t = toneClasses[tone]
  const Wrapper = onClick ? "button" : "div"

  if (loading) {
    return (
      <div
        className={cn(
          "rounded-lg border border-line bg-card p-4 sm:p-5 shadow-sm",
          className
        )}
      >
        <Skeleton className="h-10 w-10 rounded-md mb-3" />
        <Skeleton className="h-8 w-24 mb-2" />
        <Skeleton className="h-4 w-32" />
      </div>
    )
  }

  return (
    <Wrapper
      onClick={onClick}
      type={onClick ? "button" : undefined}
      className={cn(
        "block w-full text-left rounded-lg border border-line bg-card p-4 sm:p-5 shadow-sm transition-shadow",
        onClick && "hover:shadow-md hover:border-neutral-300 dark:hover:border-neutral-700 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        className
      )}
    >
      <div className="flex items-start gap-3">
        {icon && (
          <span
            className={cn(
              "shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-md",
              t.bg,
              t.text
            )}
          >
            {icon}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-caption text-subtle uppercase tracking-wide">
            {title}
          </p>
          <p className="mt-1 font-display text-h1 font-semibold text-foreground leading-tight tracking-tight truncate">
            {value}
          </p>
          {(delta !== undefined && delta !== null) && (
            <p className="mt-1 text-caption text-neutral-600 dark:text-neutral-400">
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">{delta}</span>
              {deltaLabel && <span className="ml-1 text-subtle">{deltaLabel}</span>}
            </p>
          )}
        </div>
      </div>
    </Wrapper>
  )
}
